import { useEffect, useState } from 'react'
import './PWAUpdatePrompt.css'

export function PWAUpdatePrompt() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // НЕ перезагружаем автоматически при controllerchange
      // Это может вызывать белый экран при обновлении страницы
      // Вместо этого пользователь сам выберет, когда обновить

      // Проверяем наличие ожидающего service worker
      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.ready
          setSwRegistration(registration)

          // Проверяем, есть ли ожидающий service worker
          if (registration.waiting) {
            setUpdateAvailable(true)
            return
          }

          // Слушаем установку нового service worker
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            if (!newWorker) return

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Есть новая версия, но старая еще активна
                setUpdateAvailable(true)
              }
            })
          })
        } catch (error) {
          console.error('Ошибка при проверке обновлений:', error)
        }
      }

      // Проверяем обновления при загрузке
      checkForUpdates()

      // Периодически проверяем обновления (каждые 60 секунд)
      const interval = setInterval(() => {
        if (swRegistration) {
          swRegistration.update()
        }
      }, 60000)

      return () => clearInterval(interval)
    }
  }, [swRegistration])

  const handleUpdate = async () => {
    // Защита от множественных нажатий
    if (updating) {
      console.log('⚠️ Update already in progress, ignoring click')
      return
    }
    
    if (!swRegistration?.waiting) {
      console.warn('⚠️ No waiting service worker found')
      return
    }
    
    // Блокируем кнопку
    setUpdating(true)
    
    try {
      console.log('🔄 Starting update process...')
      
      const waitingWorker = swRegistration.waiting
      
      // Слушаем изменение контроллера ПЕРЕД отправкой сообщения
      let controllerChanged = false
      const handleControllerChange = () => {
        console.log('✅ Controller changed, new service worker is active')
        controllerChanged = true
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
      }
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
      
      // Отправляем сообщение service worker для пропуска ожидания
      console.log('📤 Sending SKIP_WAITING message to service worker')
      waitingWorker.postMessage({ type: 'SKIP_WAITING' })
      
      // Ждем изменения контроллера с таймаутом
      const timeout = 2000 // 2 секунды
      const startTime = Date.now()
      
      while (!controllerChanged && (Date.now() - startTime) < timeout) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      
      // Удаляем слушатель
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
      
      // Перезагружаем страницу (всегда, даже если событие не сработало)
      console.log('🔄 Reloading page to apply update...')
      window.location.reload()
      
    } catch (error) {
      console.error('❌ Error during update:', error)
      // В случае ошибки все равно перезагружаем
      window.location.reload()
    } finally {
      // На случай если что-то пойдет не так, разблокируем через 5 секунд
      // (но обычно страница уже перезагрузится)
      setTimeout(() => setUpdating(false), 5000)
    }
  }

  if (!updateAvailable) return null

  return (
    <div className="pwa-update-prompt">
      <div className="pwa-update-content">
        <p>Dostupná nová verze aplikace!</p>
        <div className="pwa-update-buttons">
          <button 
            onClick={handleUpdate} 
            className="pwa-update-button"
            disabled={updating}
            style={updating ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
          >
            {updating ? '⏳ Aktualizace...' : 'Aktualizovat'}
          </button>
          <button 
            onClick={() => setUpdateAvailable(false)} 
            className="pwa-update-dismiss"
            disabled={updating}
          >
            Později
          </button>
        </div>
      </div>
    </div>
  )
}

